/**
 * PWAInstallPrompt — tasteful, dismissable banner.
 *
 * Behaviour:
 * 1. Listens for the browser's `beforeinstallprompt` event and stashes it.
 * 2. Shows once when the user has actually used the app a bit (we trigger
 *    it manually via `localStorage.setItem('pwa-prompt-ready','1')` after
 *    the first successful voice turn).
 * 3. iOS Safari has no `beforeinstallprompt`, so we render an info-only
 *    banner with "Add to Home Screen" instructions when on iOS + standalone
 *    is false.
 * 4. Dismissal is sticky for 30 days via `localStorage`.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Download, Share, X } from 'lucide-react';

const DISMISS_KEY = 'derwent_pwa_dismissed_at';
const READY_KEY = 'derwent_pwa_prompt_ready';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator?.standalone === true
  );
};

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  // Capture beforeinstallprompt as soon as the page loads.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isStandalone()) return;

    const recentlyDismissed = () => {
      try {
        const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
        return ts && Date.now() - ts < DISMISS_TTL_MS;
      } catch (_e) { return false; }
    };

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Only show if the app has signalled the user is "engaged".
      if (!recentlyDismissed() && localStorage.getItem(READY_KEY) === '1') {
        setVisible(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS path — no event, just render instructions when engaged.
    const iosCheck = () => {
      if (isIOS() && !recentlyDismissed() && localStorage.getItem(READY_KEY) === '1') {
        setIosHint(true);
        setVisible(true);
      }
    };
    // Re-check whenever READY_KEY might flip (poll briefly after mount).
    const poll = setInterval(() => {
      if (localStorage.getItem(READY_KEY) === '1') {
        if (deferredPrompt && !recentlyDismissed() && !visible) setVisible(true);
        iosCheck();
      }
    }, 1500);

    iosCheck();

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearInterval(poll);
    };
  }, [deferredPrompt, visible]);

  const onInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch (_e) { /* ignore */ }
    setDeferredPrompt(null);
    setVisible(false);
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* ignore */ }
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Install Hey Derwent to your home screen"
      className="fixed top-4 left-4 right-4 md:left-auto md:right-6 md:w-[380px] z-[90] bg-[var(--bg-page)] border border-[var(--surface-border)] rounded-2xl shadow-2xl p-4 flex items-start gap-3 animate-in slide-in-from-top duration-300"
      data-testid="pwa-install-prompt"
    >
      <div className="h-10 w-10 rounded-xl bg-[var(--crm-blue)]/10 text-[var(--crm-blue)] flex items-center justify-center shrink-0">
        {iosHint ? <Share className="w-5 h-5" /> : <Download className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[var(--text-main)]">Add Hey Derwent to your home screen</p>
        {iosHint ? (
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Tap the <Share className="inline w-3 h-3 mx-0.5 -mt-0.5" /> Share button in Safari, then choose <strong className="text-[var(--text-main)]">Add to Home Screen</strong>.
          </p>
        ) : (
          <p className="text-xs text-[var(--text-muted)] mt-1">
            One-tap voice access from your phone — no browser needed.
          </p>
        )}
        {!iosHint && (
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={onInstall}
              className="px-3.5 py-1.5 rounded-full bg-[var(--crm-blue)] text-white text-xs font-semibold hover:opacity-90 transition-opacity"
              data-testid="pwa-install-btn"
            >
              Install
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="px-3 py-1.5 rounded-full text-[var(--text-muted)] hover:text-[var(--text-main)] text-xs font-medium transition-colors"
              data-testid="pwa-dismiss-btn"
            >
              Not now
            </button>
          </div>
        )}
      </div>
      {iosHint && (
        <button
          type="button"
          onClick={dismiss}
          className="p-1.5 rounded-full text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
          aria-label="Dismiss"
          data-testid="pwa-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default PWAInstallPrompt;

/** Call this once the user has had a meaningful interaction (e.g. after
 * their first voice turn) so the PWA prompt actually appears. */
export const markPwaReady = () => {
  try { localStorage.setItem(READY_KEY, '1'); } catch (_e) { /* ignore */ }
};
